import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

import { useNavigate } from "react-router-dom";

function UserDashboard() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true); // Prevents rapid redirect on page refresh
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 5;

  const navigate = useNavigate();

  // 1. 🔒 Fetch and verify current user role directly from Supabase Auth
 useEffect(() => {
  const checkAuth = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error || !user) {
      navigate("/");
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from("users")
      .select("*")
      .eq("auth_id", user.id)
      .single();

    if (profileError || !profile) {
      console.error(profileError);
      navigate("/");
      return;
    }

    console.log("", profile);

    setCurrentUser(profile);

    if (profile.role !== "super_admin") {
  setLoadingAuth(false);
  navigate("/gis-data");
  return;
}

setLoadingAuth(false);
  };

  checkAuth();
}, [navigate]);

  // 2. 📥 Fetch users only if authentication verification checks out
  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .order("last_login", { ascending: false });

    if (!error) {
      setUsers(data || []);
      setFilteredUsers(data || []);
    } else {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!loadingAuth && currentUser) {
      fetchUsers();
    }
  }, [loadingAuth, currentUser]);

  // 🔍 Search + Filter
  useEffect(() => {
    let temp = [...users];

    // Filter
    if (filter === "online") {
      temp = temp.filter((u) => u.is_online);
    } else if (filter === "offline") {
      temp = temp.filter((u) => !u.is_online);
    }

    // Search
    if (search) {
      temp = temp.filter(
        (u) =>
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredUsers(temp);
    setCurrentPage(1);
  }, [search, filter, users]);

  // 📄 Pagination Calculation
  const indexOfLast = currentPage * usersPerPage;
  const indexOfFirst = indexOfLast - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Return early if still confirming identity credentials
  if (loadingAuth || !currentUser) {
    return <p style={{ padding: "20px" }}>Verifying permissions...</p>;
  }

  const onlineUsers = users.filter((u) => u.is_online);
  const offlineUsers = users.filter((u) => !u.is_online);
return (
  <div style={{ padding: "20px" }}>
    <h2>👥 User Activity Dashboard</h2>

    {/* 📊 Stats */}
    <div style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
      <div style={card}>Total: {users.length}</div>
      <div style={card}>Online: {onlineUsers.length}</div>
      <div style={card}>Offline: {offlineUsers.length}</div>
    </div>

    {/* 🔍 Search + Filter Inputs */}
    <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
      <input
        type="text"
        placeholder="Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={input}
      />

      <select
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={input}
      >
        <option value="all">All</option>
        <option value="online">Online</option>
        <option value="offline">Offline</option>
      </select>
    </div>

    {loading && <p>Loading users...</p>}

    {!loading && (
      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#007bff", color: "#fff" }}>
              <th style={th}>Name</th>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={th}>Status</th>
              <th style={th}>Online</th>
              <th style={th}>Last Login</th>
            </tr>
          </thead>

          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={td}>{u.name}</td>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.role}</td>
                  <td style={td}>{u.status}</td>
                  <td style={td}>
                    {u.is_online ? "🟢 Online" : "🔴 Offline"}
                  </td>
                  <td style={td}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "#777",
                  }}
                >
                  No users matched your search terms.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}

    {totalPages > 1 && (
      <div style={{ marginTop: "15px" }}>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            style={{
              margin: "5px",
              padding: "5px 10px",
              background: currentPage === i + 1 ? "#007bff" : "#ccc",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            {i + 1}
          </button>
        ))}
      </div>
    )}
  </div>
);
}

/* 🎨 Styles */
const card = {
  padding: "10px 15px",
  background: "#e3f2fd",
  borderRadius: "8px",
  fontWeight: "bold",
};

const input = {
  padding: "8px",
  borderRadius: "5px",
  border: "1px solid #ccc",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
};

const th = {
  padding: "10px",
  textAlign: "left",
};

const td = {
  padding: "10px",
};

export default UserDashboard;